/**
 * ExportCredibilityReport - FDA Credibility Export Button Component
 * ============================================================================
 * DC-938: Reusable dropdown button for exporting FDA Credibility reports.
 *
 * Features:
 * - Dropdown button with format options (JSON, PDF)
 * - Loading state during export
 * - Success state with auto-reset
 * - Error handling with message display
 * - Disabled state when no assessment exists
 *
 * Placement:
 * - FDA Credibility Summary card header
 * - Journey Dashboard actions menu
 * - Dedicated FDA section
 *
 * Pattern: Follows ExportPanel.tsx for status handling
 *
 * @version 1.0.0
 * @created 2026-01-13
 * ============================================================================
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import './export-credibility-report.css';

// ============================================================================
// Types
// ============================================================================

export type ExportFormat = 'json' | 'pdf';

export type ExportStatus = 'idle' | 'exporting' | 'success' | 'error';

export interface ExportCredibilityReportProps {
  /** Journey ID to export assessment for */
  journeyId: string;
  /** Disable button when no assessment exists */
  disabled?: boolean;
  /** Compact mode for smaller placements */
  compact?: boolean;
  /** Callback after successful export */
  onExportComplete?: (format: ExportFormat) => void;
  /** Callback on export error */
  onExportError?: (error: Error, format: ExportFormat) => void;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const EXPORT_FORMATS: { format: ExportFormat; label: string; icon: string; description: string }[] = [
  {
    format: 'pdf',
    label: 'PDF Report',
    icon: '📄',
    description: 'FDA-compliant credibility report',
  },
  {
    format: 'json',
    label: 'JSON Data',
    icon: '{ }',
    description: 'Machine-readable assessment data',
  },
];

const SUCCESS_RESET_DELAY = 3000;
const ERROR_RESET_DELAY = 5000;

// ============================================================================
// Main Component
// ============================================================================

export const ExportCredibilityReport: React.FC<ExportCredibilityReportProps> = ({
  journeyId,
  disabled = false,
  compact = false,
  onExportComplete,
  onExportError,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [_activeFormat, setActiveFormat] = useState<ExportFormat | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ============================================================================
  // Export Handler
  // ============================================================================

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (disabled || status === 'exporting') return;

      setIsOpen(false);
      setStatus('exporting');
      setActiveFormat(format);
      setErrorMessage(null);

      try {
        const endpoint = `/api/v1/lenses/fda-credibility/${journeyId}/export/${format}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Export failed: ${response.statusText}`);
        }

        // Get filename from Content-Disposition header or generate default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `fda-credibility-${journeyId}-${Date.now()}.${format}`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Download the file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // Success state
        setStatus('success');
        onExportComplete?.(format);

        // Reset to idle after delay
        setTimeout(() => {
          setStatus('idle');
          setActiveFormat(null);
        }, SUCCESS_RESET_DELAY);
      } catch (err) {
        console.error(`[ExportCredibilityReport] ${format.toUpperCase()} export failed:`, err);

        const error = err instanceof Error ? err : new Error('Unknown export error');
        setStatus('error');
        setErrorMessage(error.message);
        onExportError?.(error, format);

        // Reset to idle after delay
        setTimeout(() => {
          setStatus('idle');
          setActiveFormat(null);
          setErrorMessage(null);
        }, ERROR_RESET_DELAY);
      }
    },
    [journeyId, disabled, status, onExportComplete, onExportError]
  );

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const getButtonLabel = (): string => {
    switch (status) {
      case 'exporting':
        return compact ? '...' : 'Exporting...';
      case 'success':
        return compact ? '✓' : 'Downloaded!';
      case 'error':
        return compact ? '!' : 'Failed';
      default:
        return compact ? '↓' : 'Export';
    }
  };

  const getButtonIcon = (): React.ReactNode => {
    if (status === 'exporting') {
      return (
        <svg
          className="ecr-spinner"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
          <path d="M12 2a10 10 0 0110 10" strokeLinecap="round" />
        </svg>
      );
    }

    if (status === 'success') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      );
    }

    if (status === 'error') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      );
    }

    // Default: download icon
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div
      ref={dropdownRef}
      className={`ecr-container ${compact ? 'ecr-container--compact' : ''} ${className}`}
    >
      {/* Main Button */}
      <button
        className={`ecr-button ecr-button--${status}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || status === 'exporting'}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Export FDA Credibility Report"
        title={disabled ? 'No assessment available to export' : 'Export FDA Credibility Report'}
      >
        <span className="ecr-button__icon">{getButtonIcon()}</span>
        {!compact && <span className="ecr-button__label">{getButtonLabel()}</span>}
        {!compact && status === 'idle' && (
          <svg
            className={`ecr-button__caret ${isOpen ? 'ecr-button__caret--open' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && status === 'idle' && (
        <div className="ecr-dropdown" role="listbox">
          {EXPORT_FORMATS.map(({ format, label, icon, description }) => (
            <button
              key={format}
              className="ecr-dropdown__item"
              onClick={() => handleExport(format)}
              role="option"
              aria-selected={false}
            >
              <span className="ecr-dropdown__icon">{icon}</span>
              <div className="ecr-dropdown__content">
                <span className="ecr-dropdown__label">{label}</span>
                {!compact && (
                  <span className="ecr-dropdown__description">{description}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Error Tooltip */}
      {status === 'error' && errorMessage && (
        <div className="ecr-error-tooltip" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ExportCredibilityReport;
